import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { CheckCircle2, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react'
import { useUpdateStoreSettings, useTestConnection, type StoreSettings } from '../hooks/useStoreSettings'
import { toast } from 'sonner'

// Validation schema for WooCommerce credentials
const wooCommerceSchema = z.object({
  wooUrl: z.string().url('Please enter a valid URL').min(1, 'Store URL is required'),
  wooKey: z.string().optional(),
  wooSecret: z.string().optional(),
})

type WooCommerceFormData = z.infer<typeof wooCommerceSchema>

interface WooCommerceFormProps {
  settings: StoreSettings | undefined
}

export function WooCommerceForm({ settings }: WooCommerceFormProps) {
  const [showKey, setShowKey] = useState(false)
  const [showSecret, setShowSecret] = useState(false)

  const updateSettings = useUpdateStoreSettings()
  const testConnection = useTestConnection()

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    getValues,
  } = useForm<WooCommerceFormData>({
    resolver: zodResolver(wooCommerceSchema),
    defaultValues: {
      wooUrl: settings?.wooUrl || '',
      wooKey: '',
      wooSecret: '',
    },
  })

  const onSubmit = async (data: WooCommerceFormData) => {
    try {
      // Only send non-empty credentials
      const payload: { wooUrl?: string; wooKey?: string; wooSecret?: string } = {}
      if (data.wooUrl) payload.wooUrl = data.wooUrl
      if (data.wooKey) payload.wooKey = data.wooKey
      if (data.wooSecret) payload.wooSecret = data.wooSecret

      await updateSettings.mutateAsync(payload)
      toast.success('Settings saved successfully')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save settings')
    }
  }

  const handleTestConnection = async () => {
    const values = getValues()

    try {
      const result = await testConnection.mutateAsync({
        wooUrl: values.wooUrl || undefined,
        wooKey: values.wooKey || undefined,
        wooSecret: values.wooSecret || undefined,
      })

      if (result.success) {
        toast.success(result.message || `Connected! Found ${result.productCount} products.`)
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Connection test failed')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          WooCommerce Connection
          {settings?.wooKeySet && settings?.wooSecretSet && (
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          )}
        </CardTitle>
        <CardDescription>
          Connect your WooCommerce store to import products
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Store URL */}
          <div className="space-y-2">
            <Label htmlFor="wooUrl">Store URL</Label>
            <Input
              id="wooUrl"
              placeholder="https://yourstore.com"
              {...register('wooUrl')}
            />
            {errors.wooUrl && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.wooUrl.message}
              </p>
            )}
          </div>

          {/* Consumer Key */}
          <div className="space-y-2">
            <Label htmlFor="wooKey">Consumer Key</Label>
            <div className="relative">
              <Input
                id="wooKey"
                type={showKey ? 'text' : 'password'}
                placeholder={settings?.wooKeySet ? '••••••••••••••••' : 'ck_xxxxxxxx'}
                {...register('wooKey')}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => setShowKey(!showKey)}
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {settings?.wooKeySet && (
              <p className="text-xs text-muted-foreground">
                Key is set. Leave blank to keep existing.
              </p>
            )}
          </div>

          {/* Consumer Secret */}
          <div className="space-y-2">
            <Label htmlFor="wooSecret">Consumer Secret</Label>
            <div className="relative">
              <Input
                id="wooSecret"
                type={showSecret ? 'text' : 'password'}
                placeholder={settings?.wooSecretSet ? '••••••••••••••••' : 'cs_xxxxxxxx'}
                {...register('wooSecret')}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => setShowSecret(!showSecret)}
              >
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {settings?.wooSecretSet && (
              <p className="text-xs text-muted-foreground">
                Secret is set. Leave blank to keep existing.
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleTestConnection}
              disabled={testConnection.isPending}
            >
              {testConnection.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              Test Connection
            </Button>
            <Button type="submit" disabled={updateSettings.isPending || !isDirty}>
              {updateSettings.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Credentials
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
